import { Temporal } from "@/Temporal"
import Icon from "@/components/Icon"
import BootstrapButton from "@/components/form/BootstrapButton"
import { showError } from "@/composition/useLoading"
import { LatLng } from "@/models"
import type { IncidentLocation } from "@/models/IncidentLocation"
import usePopups from "@/stores/popupsStore"
import { faMap, faPlusCircle } from "@fortawesome/free-solid-svg-icons"
import { computed, ref } from "vue"
import { Alert, Modal, ModalTitle, defineComponent, type ReactiveComponent } from "vue-utils"
import { useIncidentHandler } from "../../IncidentLogHandler"
import AdditionalLocationsTable from "./AdditionalLocationsTable"
import AllLocationsMap from "./AllLocationsMap"
import PickLocationFromMap from "./PickLocationFromMap"
import { generateMarkerFilter } from "./generateMarkerFilter"

const AdditionalIncidentLocations: ReactiveComponent = () => {
	const handler = useIncidentHandler()
	const showNewLocationMap = ref(false)
	const showAllLocationsMap = ref(false)
	const submitting = ref(false)

	const popups = usePopups()

	const allPositions = computed(() => {
		const positions = [...handler.incident.positions]
		positions.sort((p1, p2) => p1.receivedAt.compareTo(p2.receivedAt))
		return positions
	})
	const additionalPositions = computed(() => {
		const positions = allPositions.value
		return positions.length === 0 ? [] : positions.slice(1)
	})

	const changeBuilder = handler.changeBuilder

	const blankLatLng = {
		latitude: NaN,
		longitude: NaN,
	}

	async function setLocation(loc: LatLng) {
		const confirmation = await popups.confirm({
			content: () => (
				<Alert title="Confirm Add Additional Location?">
					Please confirm that this additional location is correct.
					<br />
					This cannot be changed or removed later.
				</Alert>
			),
			confirmText: "Add",
		})

		if (!confirmation) {
			return
		}

		const newPosition: IncidentLocation = {
			id: 0,
			receivedAt: Temporal.Now.instant(),
			position: loc,
		}
		
		const action = changeBuilder.at("positions").id(0).add(newPosition)
		submitting.value = true
		try {
			await handler.awaitChange(action)
			showNewLocationMap.value = false
		} catch (e) {
			void showError(e as Error)
		} finally {
			submitting.value = false
		}
	}

	const nextMarkerFilter = computed(() => {
		const nextIndex = additionalPositions.value.length + 1
		return generateMarkerFilter(nextIndex)
	})

	return () => (
		<div class="flex flex-col spacing-4">
			<AdditionalLocationsTable locations={additionalPositions.value} />
			<div class="inline-flex spacing-2">
				{!handler.readOnly && (
					<BootstrapButton color="success" type="button" onClick={() => (showNewLocationMap.value = true)}>
						<Icon icon={faPlusCircle} />
						Add Additional Location
					</BootstrapButton>
				)}
				{additionalPositions.value.length > 0 && (
					<BootstrapButton color="primary" type="button" onClick={() => (showAllLocationsMap.value = true)}>
						<Icon icon={faMap} />
						View All Locations On Map
					</BootstrapButton>
				)}
			</div>

			{showNewLocationMap.value && (
				<PickLocationFromMap
					showLatLngInputs={true}
					position={blankLatLng}
					setLocation={(loc) => void setLocation(loc)}
					cancel={() => (showNewLocationMap.value = false)}
					isReadOnly={handler.readOnly}
					isSubmitting={submitting.value}
					markerFilter={nextMarkerFilter.value}
				/>
			)}

			{showAllLocationsMap.value && (
				<Modal zIndex={4} style={{ width: "80%", height: "90%" }} class="flex flex-col">
					<ModalTitle title="View Incident Locations" />
					<hr />
					<AllLocationsMap locations={allPositions.value} />
					<hr />
					<div class="flex items-center justify-end spacing-4">
						<BootstrapButton color="secondary" onClick={() => (showAllLocationsMap.value = false)}>
							Close
						</BootstrapButton>
					</div>
				</Modal>
			)}
		</div>
	)
}

export default defineComponent(AdditionalIncidentLocations)
